import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import { DashboardShell } from "../components/layout/DashboardShell";
import { DashboardPage } from "../features/document/DashboardPage";
import { WorkspacePage } from "../features/document/WorkspacePage";
import { GlobalChatPage } from "../features/chat/GlobalChatPage";
import { ClassesDashboard } from "../features/homework/ClassesDashboard";
import { ClassDetailPage } from "../features/homework/ClassDetailPage";
import { AssignmentWorkspace } from "../features/homework/AssignmentWorkspace";
import { SettingsPage } from "../features/settings/SettingsPage";
import { LandingPage } from "../features/landing/LandingPage";
import { LoginPage } from "../features/auth/LoginPage";
import { DemoWorkspace } from "../features/demo/DemoWorkspace";
import { OnboardingFlow, useOnboardingState } from "../features/onboarding/OnboardingFlow";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { useAuthStore } from "../stores/auth";
import { CommandPalette } from "../components/ui/CommandPalette";
import { api } from "../lib/api";

function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, initialized } = useAuthStore();
  const { showOnboarding, completeOnboarding } = useOnboardingState();
  if (!initialized) return null;
  if (!user) return <Navigate to="/landing" replace />;
  if (showOnboarding) return <OnboardingFlow onComplete={completeOnboarding} />;
  return <>{children}</>;
}

function OAuthCallback() {
  const { user, initialized } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!initialized) return;
    if (user) {
      navigate("/", { replace: true });
    } else {
      const timeout = setTimeout(() => navigate("/login", { replace: true }), 5000);
      return () => clearTimeout(timeout);
    }
  }, [user, initialized, navigate]);

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-black">
      <div className="flex flex-col items-center gap-4">
        <div className="h-5 w-5 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
        <p
          className="text-white/40"
          style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase" }}
        >
          Completing sign in...
        </p>
      </div>
    </div>
  );
}

function SharedRedirect() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    api.get<{ project_id: string }>(`/api/v1/shared/${token}`)
      .then((data) => {
        navigate(`/project/${data.project_id}`, { replace: true });
      })
      .catch(() => {
        setError("This share link is invalid or has expired.");
      });
  }, [token, navigate]);

  if (error) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-[var(--bg-primary)]">
        <div className="text-center">
          <p className="text-[13px] text-[var(--text-primary)] mb-2">{error}</p>
          <button onClick={() => navigate("/")} className="text-[12px] underline text-[var(--text-muted)]">
            Go to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-[var(--bg-primary)]">
      <p className="text-[11px] text-[var(--text-muted)]">Loading shared project...</p>
    </div>
  );
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/landing" element={<ErrorBoundary><LandingPage /></ErrorBoundary>} />
        <Route path="/login" element={<ErrorBoundary><LoginPage /></ErrorBoundary>} />
        <Route path="/demo" element={<ErrorBoundary><DemoWorkspace /></ErrorBoundary>} />
        <Route path="/auth/callback" element={<OAuthCallback />} />

        {/* Dashboard routes share sidebar shell */}
        <Route
          element={
            <AuthGate>
              <DashboardShell />
            </AuthGate>
          }
        >
          <Route path="/" element={<ErrorBoundary><DashboardPage /></ErrorBoundary>} />
          <Route path="/classes" element={<ErrorBoundary><ClassesDashboard /></ErrorBoundary>} />
          <Route path="/classes/:classId" element={<ErrorBoundary><ClassDetailPage /></ErrorBoundary>} />
          <Route path="/settings" element={<ErrorBoundary><SettingsPage /></ErrorBoundary>} />
        </Route>

        {/* Chat uses its own full-screen layout */}
        <Route
          path="/chat"
          element={
            <AuthGate>
              <ErrorBoundary><GlobalChatPage /></ErrorBoundary>
            </AuthGate>
          }
        />

        {/* Workspace routes use their own full-screen layout */}
        <Route
          path="/project/:projectId"
          element={
            <AuthGate>
              <WorkspacePage />
            </AuthGate>
          }
        />
        <Route
          path="/classes/:classId/assignments/:assignmentId"
          element={
            <AuthGate>
              <ErrorBoundary><AssignmentWorkspace /></ErrorBoundary>
            </AuthGate>
          }
        />
        <Route path="/shared/:token" element={<SharedRedirect />} />
        <Route path="*" element={<Navigate to="/landing" replace />} />
      </Routes>
      <CommandPalette />
    </BrowserRouter>
  );
}
